import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { useAccessToken } from '../../components/common/hooks/useAccessToken';
import Input from '../../components/common/simple/input';
import { showLoader, hideLoader } from '../../components/store/common/slice';
import BllinkUtils from '../../components/common/utils/utils';
import { selectCustomStyles } from '../../components/common/shared/select.styled';
import { changePartnersList } from '../../components/store/admin/slice';
import BuildingTitle from '../../components/common/buildingTitle';
import { StyledDropdownWrapper } from '../components/PartnersAndBuildingsDropdowns/PartnersAndBuildingsDropdowns.styled';
import { StyledEditWrapper } from './EditBuilding.styled';

const EditBuilding = ({ match }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const partnersList = useSelector(state => state.admin.partnersList);
    let { buildingID } = match.params;

    buildingID = buildingID || 1111;
    const [currentPartner, setCurrentPartner] = useState();
    const [defaultPartner, setDefaultPartner] = useState();
    const [currentUsers, setCurrentUsers] = useState();
    const [defaultUsers, setDefaultUsers] = useState([]);
    const [partnersOptions, setPartnersOptions] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    const [defaultManagerValues, setDefaultManagerValues] = useState({
        phone: '',
        email: '',
        managerFirstName: '',
        managerLastName: ''
    });
    const [managerInputValues, setManagerInputValues] = useState({
        phone: '',
        email: '',
        managerFirstName: '',
        managerLastName: ''
    });
    const [defaultCommissionValues, setDefaultCommissionValues] = useState({
        tenant: 0,
        building: 0,
        managementCompany: 0
    });
    const [commissionInputValues, setCommissionInputValues] = useState({
        tenant: 0,
        building: 0,
        managementCompany: 0,
    });
    const [showCommissionError, setShowCommissionError] = useState(false);
    const [assignBtnDisabled, setAssignBtnDisabled] = useState(true);
    const [managerBtnDisabled, setManagerBtnDisabled] = useState(true);
    const [commissionBtnDisabled, setCommissionBtnDisabled] = useState(true);

    //get data
    useEffect(() => {
        if (!client) return;
        (async function () {
            dispatch(showLoader());
            if (partnersList.length > 0) {
                setPartnersOptions(partnersList)
            } else {
                const { partners } = await client.get('/operations/data/partners') || {partners: []};
                const optionsArr = partners.map(item => ({
                    value: item.partnerID,
                    label: item.partnerName,
                    disabled: item.disabled
                }));
                setPartnersOptions(optionsArr);
                dispatch(changePartnersList(optionsArr));
            }

            const { users } = await client.get('/operations/data/users') || { users: null };
            if (users) {
                const options = users.map(({ userID }) => ({ value: userID, label: userID }));
                setUsersOptions(options);
            }

            const { building } = await client.managers.buildings.getManagerBuilding(
                buildingID, {skipStats: true, includeInternalID: true, forceNoCache: true}
            ) ?? { building: {} };

            if (building) {
                const { Commission, phone, email, firstName, lastName, partnerID, existingUsersAndRoles } = building;
                setDefaultCommissionValues({
                    tenant: +Commission?.tenant,
                    building: +Commission?.building,
                    managementCompany: +Commission?.managementCompany
                });
                setCommissionInputValues({
                    tenant: +Commission?.tenant,
                    building: +Commission?.building,
                    managementCompany: +Commission?.managementCompany
                });

                setDefaultManagerValues({
                    phone: phone,
                    email: email,
                    managerFirstName: firstName,
                    managerLastName: lastName
                });
                setManagerInputValues({
                    phone: phone,
                    email: email,
                    managerFirstName: firstName,
                    managerLastName: lastName
                });

                if (partnerID) setDefaultPartner(partnerID);

                if (existingUsersAndRoles) {
                    let users = [];
                    existingUsersAndRoles.map(user => users.push(user.userID));
                    setDefaultUsers(users);
                };
            }
            dispatch(hideLoader());
        })();
    }, [client]);

    useEffect(() => {
        if (defaultPartner) {
            const partnerOption = partnersOptions.filter(partner => partner.value === defaultPartner);
            if (partnerOption) setCurrentPartner(partnerOption[0]);
        }
    }, [defaultPartner]);

    useEffect(() => {
        if (defaultUsers) {
            const users = defaultUsers.map(user => ({ value: user, label: user }));
            setCurrentUsers(users)
        }
    }, [defaultUsers]);

    //disable or enable update btns
    useEffect(() => {
        if (currentPartner && currentPartner?.value !== defaultPartner) setAssignBtnDisabled(false);
        else setAssignBtnDisabled(true);
    }, [currentPartner]);

    useEffect(() => {
        let currentUsersIds = [];
        if (currentUsers) {
            currentUsers.forEach(item => currentUsersIds.push(item.value));
            const isSame = (
                defaultUsers.length === currentUsersIds.length && defaultUsers.every(element => {
                    if(currentUsersIds.indexOf(element)>-1){
                        return element = currentUsersIds[currentUsersIds.indexOf(element)];
                    }
                    return undefined;
                })
            );
            setAssignBtnDisabled(isSame);
        }
    }, [currentUsers]);

    useEffect(() => {
        if (JSON.stringify(managerInputValues) !== JSON.stringify(defaultManagerValues)) setManagerBtnDisabled(false);
        else setManagerBtnDisabled(true);
    }, [managerInputValues]);

    useEffect(() => {
        if (JSON.stringify(commissionInputValues) !== JSON.stringify(defaultCommissionValues)) setCommissionBtnDisabled(false);
        else setCommissionBtnDisabled(true);
    }, [commissionInputValues]);

    //handle inputs and dropdowns
    const onPartnerDropdownChange = e => setCurrentPartner(e);

    const onUserDropdownChange = e => setCurrentUsers(e);

    const onCreatingNewUserDropdownOption = value => {
        setUsersOptions([
            ...usersOptions,
            {
                value: value,
                label: value
            }
        ]);
        setCurrentUsers([
            ...currentUsers,
            {
                value: value,
                label: value
            }
        ]);
    };

    const onCommissionInputsChange = e => {
        const targ = e.target;
        setCommissionInputValues({
            ...commissionInputValues,
            [targ.name]: targ.value
        })
    };

    const onManagerInputsChange = e => {
        const targ = e.target;
        setManagerInputValues({
            ...managerInputValues,
            [targ.name]: targ.value
        })
    };

    //btns functionality
    const onAssignClick = async () => {
        dispatch(showLoader());
        let payload;
        if (currentPartner && currentPartner.value !== defaultPartner) {
            payload = {
                assignTo: {
                    partnerID: currentPartner.value
                }
            }
        } else {
            const addedUser = currentUsers.map(item => {
                if (defaultUsers.indexOf(item.value) < 0) {
                    return item.value;
                }
                return undefined;
            });
            payload = {
                assignTo: {
                    userID: addedUser
                }
            }
        }
        const result = await client.post(`/managers/buildings/${buildingID}`, payload);

        if (result) {
            NotificationManager.success(t('editBuilding.success.manager'));
            setTimeout(() => window.location.reload(), 2000);
        } else NotificationManager.error(t('buildings.something_wrong'));
        dispatch(hideLoader());
    };

    const onUpdateManagerClick = async () => {
        dispatch(showLoader());
        if (!BllinkUtils.JsExtras.emailValidator(managerInputValues.email)) {
            NotificationManager.error(t('editBuilding.errors.email'));
            return
        }
        if (!BllinkUtils.JsExtras.phoneValidator(null, managerInputValues.phone, {minLength: 9, maxLength: 10})) {
            NotificationManager.error(t('editBuilding.errors.phone'));
            return
        }

        const payload = {
            managerDetails: {
                managerEmail: managerInputValues.email,
                managerFirstName: managerInputValues.managerFirstName,
                managerLastName: managerInputValues.managerLastName,
                managerPhone: managerInputValues.phone
            }
        };
        const result = await client.post(`/managers/buildings/${buildingID}`, payload);

        if (result) {
            NotificationManager.success(t('editBuilding.success.managerDetails'));
            setTimeout(() => window.location.reload(), 2000);
        } else NotificationManager.error(t('buildings.something_wrong'));
        dispatch(hideLoader());
    };

    const onUpdateCommissionClick = async () => {
        const { tenant, building, managementCompany } = commissionInputValues;
        if (+tenant + +building + +managementCompany > 1) {
            setShowCommissionError(false);
        } else setShowCommissionError(true);

        if (!showCommissionError) {
            dispatch(showLoader());
            const payload = {
                commission: {
                    commissionTenant: +commissionInputValues.tenant,
                    commissionBuilding: +commissionInputValues.building,
                    commissionManagementCompany: +commissionInputValues.managementCompany
                }
            };
            const result = await client.post(`/managers/buildings/${buildingID}`, payload);

            if (result) {
                NotificationManager.success(t('editBuilding.success.commissionDetails'));
                setTimeout(() => window.location.reload(), 2000);
            } else NotificationManager.error(t('buildings.something_wrong'));
            dispatch(hideLoader());
        }
    };

    return (
        <>
            <BuildingTitle buildingID={buildingID} type='welcome' />
            <StyledEditWrapper>
                <div className='edit-title'>{t('editBuilding.titles.assignTo')}</div>
                <div className='inputs-wrapper'>
                    <StyledDropdownWrapper className="dropdown">
                        <label className="label">{t('editBuilding.labels.partner')}</label>
                        <Select
                            styles={selectCustomStyles}
                            options={partnersOptions}
                            value={currentPartner}
                            onChange={onPartnerDropdownChange}
                            name="partner"
                            isOptionDisabled={option => option.disabled}
                        />
                    </StyledDropdownWrapper>

                    <StyledDropdownWrapper className="dropdown">
                        <label className="label">{t('editBuilding.labels.user')}</label>
                        <Creatable
                            styles={selectCustomStyles}
                            options={usersOptions}
                            value={currentUsers}
                            onChange={onUserDropdownChange}
                            onCreateOption={onCreatingNewUserDropdownOption}
                            isMulti
                            name="user"
                        />
                    </StyledDropdownWrapper>
                </div>
                <button
                    className="bllink-button"
                    onClick={onAssignClick}
                    disabled={assignBtnDisabled}
                >
                    {t('editBuilding.btns.assign')}
                </button>

                <div className='edit-title'>{t('editBuilding.titles.updateManager')}</div>
                <div  className='inputs-wrapper'>
                    <Input
                        label={t('editBuilding.labels.email')}
                        name='email'
                        value={managerInputValues.email}
                        onChange={onManagerInputsChange}
                    />

                    <Input
                        label={t('editBuilding.labels.phone')}
                        name='phone'
                        value={managerInputValues.phone}
                        onChange={onManagerInputsChange}
                    />

                    <Input
                        label={t('editBuilding.labels.managerFirstName')}
                        name='managerFirstName'
                        value={managerInputValues.managerFirstName}
                        onChange={onManagerInputsChange}
                    />

                    <Input
                        label={t('editBuilding.labels.managerLastName')}
                        name='managerLastName'
                        value={managerInputValues.managerLastName}
                        onChange={onManagerInputsChange}
                    />
                </div>
                <button
                    className="bllink-button"
                    onClick={onUpdateManagerClick}
                    disabled={managerBtnDisabled}
                >
                    {t('editBuilding.btns.updateCreate')}
                </button>
                <div className='edit-title'>{t('editBuilding.titles.commission')}</div>
                <div className='inputs-wrapper'>
                    <Input
                        label={t('editBuilding.labels.tenant')}
                        name='tenant'
                        value={commissionInputValues.tenant}
                        onChange={onCommissionInputsChange}
                        type='number'
                        max={5}
                    />
                    <Input
                        label={t('editBuilding.labels.building')}
                        name='building'
                        value={commissionInputValues.building}
                        onChange={onCommissionInputsChange}
                        type='number'
                        max={5}
                    />
                    <Input
                        label={t('editBuilding.labels.managementCompany')}
                        name='managementCompany'
                        value={commissionInputValues.managementCompany}
                        onChange={onCommissionInputsChange}
                        type='number'
                        max={5}
                    />
                </div>
                {showCommissionError && <div className='commission-error'>{t('editBuilding.commissionError')}</div>}
                <button
                    className="bllink-button"
                    onClick={onUpdateCommissionClick}
                    disabled={commissionBtnDisabled}
                >
                    {t('editBuilding.btns.setUpdate')}
                </button>
            </StyledEditWrapper>
        </>
    )
}

export default EditBuilding;
